import { format } from "date-fns";
import { memo } from "react";

import { enums, formatters, parsers } from "@fraction/shared";

import KeyValue from "./KeyValue";
import SubmissionSection from "./SubmissionSection";

const ApplicantInformation = ({
  applicant,
  app,
  idx,
}: {
  app: parsers.application.FullApplication;
  applicant: parsers.applicant.Applicant;
  idx: number;
}) => {
  const assets = app.assets?.filter((asset) => asset.ownerEmails?.includes(applicant.email || ""));
  const debts = app.debts?.filter((debt) => debt.ownerEmails?.includes(applicant.email || ""));
  const properties = app.otherOwnedProperties?.filter((property) =>
    property.ownerEmails?.includes(applicant.email || "")
  );
  const incomes = app.incomeSources?.filter((income) => income.ownerEmails?.includes(applicant.email || ""));
  return (
    <SubmissionSection
      title={`${formatters.user.possessive(
        applicant.name ? parsers.user.parseName(applicant.name).first : `Applicant ${idx}`
      )} information`}
    >
      <KeyValue label="Name" value={applicant.name || ""} />
      <KeyValue label="Email" value={applicant.email || ""} />
      <KeyValue label="Phone" value={formatters.phone.formatPhoneNumber(applicant.phone)} />
      <KeyValue label="Home address" value={formatters.property.formattedAddress(applicant.homeAddress)} />
      {applicant.dateOfBirth && (
        <KeyValue label="Date of birth" value={format(applicant.dateOfBirth, "MM-dd-y")} />
      )}
      {applicant.race && (
        <KeyValue
          label="Race"
          value={applicant.race?.map((race) => formatters.enums.RACE_MAP[race])?.join(", ") || ""}
        />
      )}
      {applicant.ethnicity && (
        <KeyValue
          label="Ethnicity"
          value={
            applicant.ethnicity?.map((ethnicity) => formatters.enums.ETHNICITY_MAP[ethnicity])?.join(", ") ||
            ""
          }
        />
      )}
      {applicant.sex && (
        <KeyValue
          label="Sex"
          value={
            applicant.sex?.map((sex) => formatters.enums.SEX_MAP[sex])?.join(", ") ||
            formatters.enums.SEX_MAP[enums.Sex.NOT_DISCLOSED]
          }
        />
      )}
      {applicant.maritalStatus && (
        <KeyValue
          label="Marital Status"
          value={formatters.enums.MARITAL_STATUS_MAP[applicant.maritalStatus]}
        />
      )}
      {applicant.militaryStatus && (
        <KeyValue
          label="Military status"
          value={formatters.enums.MILITARY_STATUS_MAP[applicant.militaryStatus]}
        />
      )}
      {applicant.residencyStatus && (
        <KeyValue
          label="Residency status"
          value={formatters.enums.RESIDENCY_STATUS_MAP[applicant.residencyStatus]}
        />
      )}
      {applicant.bankruptcyStatus && (
        <KeyValue
          label="Bankruptcy status"
          value={formatters.enums.BANKRUPTCY_STATUS_MAP[applicant.bankruptcyStatus]}
        />
      )}
      {applicant.timeSinceBankruptcy && (
        <KeyValue label="Time since bankruptcy" value={`${applicant.timeSinceBankruptcy} years`} />
      )}
      {!!assets?.length && (
        <KeyValue
          label="Assets"
          value={`${assets.length} assets totalling ${formatters.number.getCurrencyFromNumber(
            assets.reduce((a, b) => a + (b.value || 0), 0)
          )}`}
        />
      )}
      {!!debts?.length && (
        <KeyValue
          label="Debts"
          value={`${debts.length} debts totalling ${formatters.number.getCurrencyFromNumber(
            debts.reduce((a, b) => a + (b.balance || 0), 0)
          )}`}
        />
      )}
      {!!properties?.length && (
        <KeyValue
          label="Properties"
          value={`${properties.length} properties totalling ${formatters.number.getCurrencyFromNumber(
            properties.reduce((a, b) => a + (b.value || 0), 0)
          )}`}
        />
      )}
      {!!incomes?.length && (
        <KeyValue
          label="Income"
          value={`${incomes.length} incomes totalling ${formatters.number.getCurrencyFromNumber(
            incomes.reduce((a, b) => a + (b.income || 0), 0)
          )}`}
        />
      )}
    </SubmissionSection>
  );
};

export default memo(ApplicantInformation);
